function resolveBufferSeconds(bufferSeconds) {
  if (bufferSeconds === null || bufferSeconds === undefined || bufferSeconds === "") return null;
  const buffer = Number(bufferSeconds);
  return Number.isFinite(buffer) && buffer >= 0 ? buffer : null;
}

// Compare at clock-minute precision, matching scheduled HH:mm arrival times.
// bufferSeconds is the customer delay threshold. Missing or invalid values leave
// withinBuffer unset so existing early/on-time/late results stay unchanged.
export function getScheduleDeviation(tripStatus, stopId, etaSeconds, now = Date.now(), bufferSeconds = null) {
  if (tripStatus?.occurrenceEnd <= now) return null;
  if (stopId == null || !Number.isFinite(etaSeconds) || etaSeconds < 0 ||
      !tripStatus?.scheduleId || !Array.isArray(tripStatus.scheduledStops)) return null;

  const stop = tripStatus.scheduledStops.find(
    (entry) => String(entry.stopId) === String(stopId),
  );
  if (!stop?.scheduledArrival) return null;
  const scheduled = Date.parse(stop.scheduledArrival);
  if (!Number.isFinite(scheduled) || !Number.isFinite(now)) return null;

  const predictedMs = now + etaSeconds * 1000;
  const minutes = Math.floor(predictedMs / 60000) - Math.floor(scheduled / 60000);
  const buffer = resolveBufferSeconds(bufferSeconds);
  // Match the backend LATE_TO_STOP rule: floored lateness >= threshold is late.
  const latenessSeconds = Math.floor((predictedMs - scheduled) / 1000);
  return {
    minutes: Math.abs(minutes),
    status: minutes < 0 ? "early" : minutes > 0 ? "late" : "onTime",
    withinBuffer: buffer === null ? null : latenessSeconds < buffer,
  };
}

export function formatDeviationDuration(totalMinutes, translate = (key) => key) {
  const minutes = Math.max(0, Math.floor(Number(totalMinutes) || 0));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  const minuteLabel = translate("min");
  const hourLabel = translate("hr");
  if (hours === 0) return `${remainder} ${minuteLabel}`;
  if (remainder === 0) return `${hours} ${hourLabel}`;
  return `${hours} ${hourLabel} ${remainder} ${minuteLabel}`;
}

export function getScheduleDeviationMessage(deviation) {
  if (!deviation) return null;
  if (deviation.status === "onTime") {
    return { key: "Arriving on time", values: {} };
  }
  return {
    key: `Arriving {{duration}} ${deviation.status}`,
    values: { duration: formatDeviationDuration(deviation.minutes) },
  };
}
const DAY_MS = 86400000;
const IST_OFFSET_MS = 330 * 60000;

// Existing schedule APIs supply recurrence windows; stop clock times are in IST.
export function buildScheduleTiming(schedules, now = Date.now()) {
  const candidates = schedules.filter((schedule) => schedule && !schedule.isDeleted)
    .map((schedule) => {
      const occurrence = schedule.currentOccurrence || schedule.nextOccurrence;
      return { schedule, start: Date.parse(occurrence?.start), end: Date.parse(occurrence?.end) };
    })
    .filter(({ start, end }) => Number.isFinite(start) && Number.isFinite(end) && end > now);
  const running = candidates.filter(({ start }) => start <= now).sort((a, b) => b.start - a.start);
  const upcoming = candidates.filter(({ start }) => start > now).sort((a, b) => a.start - b.start);
  const active = running[0] || upcoming[0];
  if (!active) return null;

  const day = Math.floor((active.start + IST_OFFSET_MS) / DAY_MS) * DAY_MS - IST_OFFSET_MS;
  const clockTime = (value) => {
    if (typeof value !== "string" || !/^\d{2}:\d{2}$/.test(value)) return null;
    const [hours, minutes] = value.split(":").map(Number);
    return hours < 24 && minutes < 60 ? day + (hours * 60 + minutes) * 60000 : null;
  };
  let previous = active.start;
  const scheduledStops = [...(active.schedule.stopSchedules || [])]
    .sort((a, b) => Number(a.id) - Number(b.id))
    .map((stop) => {
      let arrival = clockTime(stop.arrivalTime);
      let departure = clockTime(stop.departureTime);
      while (arrival !== null && arrival < previous - 60000) arrival += DAY_MS;
      while (departure !== null && departure < (arrival ?? previous) - 60000) departure += DAY_MS;
      previous = departure ?? arrival ?? previous;
      return { stopId: stop.stopId, scheduledArrival: arrival === null ? null : new Date(arrival).toISOString() };
    });
  return { scheduleId: active.schedule.id, scheduledStops, occurrenceEnd: active.end };
}
